import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';
// hooks
import useDialog from 'hooks/useDialog';
// Models
import IReservation from 'models/Reservation';
// Types
import ReservationStatus from 'types/ReservationStatus';
import UserRoles from 'types/UserRoles';
// Selectors
import { selectCurrentUser } from 'store/users/usersSelectors';
// MUI
import { makeStyles } from '@mui/styles';
import {
  Box, Card, Chip, Collapse, Divider, Grid, Button, IconButton, Tooltip
} from '@mui/material';
import Typography from '@mui/material/Typography';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import DoneIcon from '@mui/icons-material/Done';
import CloseIcon from '@mui/icons-material/Close';
import PaidOutlinedIcon from '@mui/icons-material/PaidOutlined';
// Components
import Collapsable from 'components/Collapsable';
import PaymentForms from 'components/PaymentForms';
// Utilities
import { formatStartEndDate } from 'utilities/DateTimeFormatter';
import { formatCash } from 'utilities/Utilities';
import { selectAccountsSettings } from 'store/accounts/accountsSelectors';

type Props = {
  reservation: IReservation,
};

const ReservationCard:React.FC<Props> = ({ reservation }) => {
  const classes = useStyles();

  const currentUser = useSelector(selectCurrentUser);
  const accountsSettings = useSelector(selectAccountsSettings);

  // handle extended
  const [ extended, setExtended ] = useState<boolean>(false);

  const toggleExtended = () => {
    setExtended(!extended);
  };

  const { Dialog, openDialog, closeDialog } = useDialog();

  return (
    <React.Fragment>
      <Dialog maxWidth="sm">
        <PaymentForms reservation={reservation} onClose={closeDialog} />
      </Dialog>
      <Card sx={{ p: 2, borderRadius: 0, backgroundColor: extended ? 'rgba(0, 0, 0, 0.04)' : '#fff', borderBottom: '1px solid #eee', width: '100%' }}>
        <Collapsable onClick={toggleExtended}>
          <Grid container spacing={2}>
            <Grid item xs={10} md={3} alignSelf="center" sx={{ order: -1 }}>
              <Typography className={classes.text}>
                {reservation.propertyName}
              </Typography>
              <Typography className={classes.title}>
                  Created: {dayjs(reservation.creationDate).format('MMMM DD YYYY')}
                </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3} alignSelf="center">
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography className={classes.title} sx={{ display: {xs: 'block', sm: 'none'} }}>Date range</Typography>
                <Typography className={classes.text}>
                  {formatStartEndDate(reservation.start, reservation.end)}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: {xs: 2, sm: 0} }}>
                <Typography className={classes.title} sx={{ display: {xs: 'block', sm: 'none'} }}>Status</Typography>
                <Box>
                  {reservation.status === ReservationStatus.Completed && <Chip color={'success'} size="small" label="Completed" icon={<DoneAllIcon />} />}
                  {reservation.status === ReservationStatus.Pending && <Chip color={'info'} size="small" label="Pending" icon={<DoneIcon />} />}
                  {reservation.status === ReservationStatus.Cancelled && <Chip color={'error'} size="small" label="Rejected" icon={<CloseIcon />} />}
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={2} sx={{ display: 'flex', alignSelf: 'center', justifyContent: 'space-between', flexDirection: { xs: 'row', sm: 'column' } }}>
              <Typography className={classes.title}>
                Price
              </Typography>
              <Typography className={classes.text}>
                {formatCash(reservation.price)}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={2} sx={{ display: 'flex', alignSelf: 'center', justifyContent: 'space-between', flexDirection: { xs: 'row', sm: 'column' } }}>
              <Typography className={classes.title}>
                Reservation number
              </Typography>
              <Typography className={classes.text}>
                {reservation.number || '-'}
              </Typography>
            </Grid>
            <Grid item xs={2} alignSelf="center" sx={{ order: { xs: -1, md: 0 } }}>
              {!!accountsSettings.paymentProvider && reservation.status !== ReservationStatus.Cancelled && currentUser?.activeRole === UserRoles.Guest && !reservation.guestPaymentDate && reservation.guestPaymentEnabled && (
                <Box  sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    sx={{ display: { xs: 'none', md: 'inline-flex' } }}
                    size="small"
                    onClick={openDialog}
                    variant="contained"
                    startIcon={<PaidOutlinedIcon />}
                  >
                    {`Pay ${formatCash(reservation.price)}`}
                  </Button>
                  <Tooltip title={`Pay ${formatCash(reservation.price)}`} sx={{ display: { xs: 'inline-flex', md: 'none' } }}>
                    <IconButton onClick={openDialog} color="primary">
                      <PaidOutlinedIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              )}
              {reservation.guestPaymentDate && currentUser?.activeRole === UserRoles.Guest && (
                <Box  sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Chip label="Paid" color="info" />
                </Box>
              )}
            </Grid>
          </Grid>
        </Collapsable>
        <Collapse in={extended}>
          <Divider sx={{ p: 1 }} />
          <Grid container spacing={2} sx={{ pt: 2 }}>
            <Grid item xs={12}>
              <Typography className={classes.title}>
                Notes
              </Typography>
              <Typography className={classes.text}>
                {reservation.notes || '-'}
              </Typography>
            </Grid>
          </Grid>
        </Collapse>
      </Card>
    </React.Fragment>
  );
}

export default ReservationCard;

const useStyles = makeStyles({
  title: {
    fontSize: '12px',
    lineHeight: '166%',
    letterSpacing: '0.4px',
    color: 'rgba(0, 0, 0, 0.6)',
  },
  text: {
    fontSize: '14px',
    lineHeight: '143%',
    letterSpacing: '0.17px',
    color: 'rgba(0, 0, 0, 0.87)',
  },
  link: {
    color: '#1976D2',
    cursor: 'pointer',
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  img: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
});
